import { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { resolveId, encryptId } from '@/lib/encryptId';
import { subjects } from '@/api/client';
import { cn } from '@/lib/cn';
import { WizardShell } from '@/components/ui/WizardShell';
import { deriveStepStatuses } from '@/components/ui/Stepper';
import { AuditTrailPanel } from '@/components/audit/AuditTrailPanel';
import { Step1Screening } from './Step1Screening';
import { Step2InformedConsent } from './Step2InformedConsent';
import { Step3Demographics } from './Step3Demographics';
import { Step5Enroll } from './Step5Enroll';
import { subjectDisplayId } from '@/lib/subjectLabel';

const STEP_DEFS = [
  { key: 'step1', label: '1. Screening' },
  { key: 'step2', label: '2. Demographics' },
  { key: 'step3', label: '3. Consent' },
  // Eligibility step removed (client, 16 Oct 2026). The key stays 'step5' so
  // nothing keyed on it (labels, resume, re-consent jump) moves.
  { key: 'step5', label: '4. Final' },
] as const;

const DEFAULT_LABELS: Record<string, string> = {
  step1: 'Next',
  step2: 'Next',
  step3: 'Generate Document',
  step5: 'Final',
};

export function SubjectOnboardingWizardPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const subjectId = resolveId(params.get('subject_id') ?? '');
  const isReConsent = params.get('reconsent') === '1';
  const forceStep = params.get('step'); // e.g. '2' when PI navigates here from dashboard queue

  const [activeKey, setActiveKey] = useState('step1');
  const [completedKeys, setCompletedKeys] = useState<Set<string>>(() => new Set());
  const seededRef = useRef(false);

  // ── Footer action wiring ────────────────────────────────────────
  const stepTrigger = useRef<(() => void) | null>(null);
  const stepSaveTrigger = useRef<(() => void) | null>(null);

  const [isStepPending, setIsStepPending] = useState(false);
  const [isStepSavePending, setIsStepSavePending] = useState(false);
  const [nextLabel, setNextLabel] = useState(DEFAULT_LABELS[activeKey] ?? 'Next');
  const [nextDisabled, setNextDisabled] = useState(false);

  useEffect(() => {
    stepTrigger.current = null;
    stepSaveTrigger.current = null;
    setIsStepPending(false);
    setNextLabel(DEFAULT_LABELS[activeKey] ?? 'Next');
    setNextDisabled(false);
    // Refresh subject data when entering consent step so active_consent_record_id
    // is always current (webhook may have updated it since the last query).
    if (activeKey === 'step3' && subjectId) {
      qc.invalidateQueries({ queryKey: ['subject', subjectId] });
    }
  }, [activeKey, subjectId, qc]);

  // ── Subject + wizard state ─────────────────────────────────────
  const subjectQ = useQuery({
    queryKey: ['subject', subjectId],
    queryFn: () => subjects.detail(subjectId),
    enabled: !!subjectId,
    staleTime: 60_000,
  });
  // Subject ID once screening records it, else REF-SUB-xxxx.
  const externalRef = subjectQ.data
    ? subjectDisplayId({
      external_ref: subjectQ.data.subject.external_ref,
      ivrf_number: subjectQ.data.subject.ivrf_number ?? subjectQ.data.wizard_state?.ivrf_number,
    })
    : undefined;
  const wizardComplete = !!subjectQ.data?.wizard_state.enrolled_at;

  // Seed completed steps and active key once from persisted wizard_state
  useEffect(() => {
    if (seededRef.current || !subjectQ.data) return;
    seededRef.current = true;

    const ws = subjectQ.data.wizard_state;
    const status = subjectQ.data.subject.status;
    // Use subject.status as an authoritative fallback for subjects whose
    // wizard_state timestamps aren't populated. 'consented' is gone from the
    // vocabulary and consent no longer writes a status at all, so this
    // fallback now only recognises enrolled-and-later; a consented subject who
    // has not been enrolled is detected by active_consent_record_id, which was
    // always the primary signal. 'screen_fail' is excluded on purpose — a
    // screen failure has not passed consent.
    // 'screened' leads both lists now: it is what the wizard's Final step
     // writes, so it is the first status that proves every step ran.
    const pastConsent = ['screened', 'enrolled', 'active', 'completed', 'withdrawn'].includes(status);
    const pastEnroll  = ['screened', 'enrolled', 'active', 'completed', 'withdrawn'].includes(status);

    const done = new Set<string>();
    // step3 is also implicitly done once screening is finalised (can't reach
    // the Final step without finishing consent, even in the no-webhook local dev
    // case where active_consent_record_id may still be null).
    const consentImpliedByLaterStep = pastEnroll;
    if (ws.screened_at || pastConsent) done.add('step1');
    if (ws.demographics_recorded_at || pastConsent) done.add('step2');
    if (ws.active_consent_record_id || pastConsent || consentImpliedByLaterStep) done.add('step3');
    // enrolled_at is NO LONGER set by the Final step — enrolment is its own
    // move now — so pastEnroll (which includes 'screened') is what marks this
    // step done. Keeping enrolled_at too costs nothing and still recognises a
    // subject enrolled before the change.
    if (ws.enrolled_at || pastEnroll) done.add('step5');

    // Re-consent mode: subject is enrolled but needs to sign the new version.
    // Mark all steps except step3 (Consent) complete so the wizard opens directly on step3.
    if (isReConsent && pastEnroll) {
      done.delete('step3');
      setCompletedKeys(done);
      setActiveKey('step3');
      return;
    }

    // ?step=N override — used when the PI navigates here from the dashboard
    // signature queue (e.g. to review a pending consent co-sign at step 2).
    // Unmark the target step so the stepper shows it as active, not done.
    if (forceStep) {
      const forceKey = `step${forceStep}`;
      if (STEP_DEFS.some((s) => s.key === forceKey)) {
        done.delete(forceKey);
        setCompletedKeys(done);
        setActiveKey(forceKey);
        return;
      }
    }

    setCompletedKeys(done);

    // Land on the first incomplete step
    const firstIncomplete = STEP_DEFS.find((s) => !done.has(s.key));
    if (firstIncomplete) setActiveKey(firstIncomplete.key);
  }, [subjectQ.data]);

  // ── Step navigation ────────────────────────────────────────────
  function handleComplete(key: string) {
    const idx = STEP_DEFS.findIndex((s) => s.key === key);
    setCompletedKeys((prev) => new Set([...prev, key]));
    // Re-consent: steps 4-5 are already done — jump straight to step5 after signing.
    if (isReConsent && key === 'step3') {
      setActiveKey('step5');
      return;
    }
    if (idx < STEP_DEFS.length - 1) {
      setActiveKey(STEP_DEFS[idx + 1]!.key);
    } else {
      // All steps done — navigate to subject detail page (AC8)
      navigate(`/subjects/${encryptId(subjectId)}`);
    }
  }

  function handleBack() {
    // Re-consent: pressing Back on step3 (Consent) exits back to subject detail.
    if (isReConsent && activeKey === 'step3') {
      navigate(`/subjects/${encryptId(subjectId)}`);
      return;
    }
    const idx = STEP_DEFS.findIndex((s) => s.key === activeKey);
    if (idx > 0) setActiveKey(STEP_DEFS[idx - 1]!.key);
  }

  const stepDefsForStepper = useMemo(
    () => deriveStepStatuses(STEP_DEFS, completedKeys, activeKey),
    [completedKeys, activeKey],
  );

  // Save & Resume Later only on steps that have a draft save API (not screening or enroll).
  const STEPS_WITH_SAVE = new Set(['step2', 'step3']);
  const showSaveAndResume = STEPS_WITH_SAVE.has(activeKey);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-[22px] font-semibold text-ink-primary">
              Subject Onboarding{externalRef ? `: ${externalRef}` : ''}
            </h1>
            {subjectQ.data && (
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                  wizardComplete
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700',
                )}
              >
                {wizardComplete ? 'Enrolled' : 'Draft Subject'}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            5-step wizard. Complete each step in order to enrol the subject.
          </p>
        </div>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => {
            qc.removeQueries({ queryKey: ['subjects'] });
            navigate('/subjects');
          }}
        >
          Exit
        </button>
      </header>

      {!subjectId ? (
        <div className="card p-8 text-center text-sm text-ink-muted">
          Open the wizard from a subject row to start.
        </div>
      ) : (
        <WizardShell
          steps={stepDefsForStepper}
          activeKey={activeKey}
          onStepClick={(key) => setActiveKey(key)}
          auditPanel={<AuditTrailPanel scope={{ subjectId }} />}
          onBack={handleBack}
          onNext={nextLabel ? () => stepTrigger.current?.() : undefined}
          onSaveAndExit={
            showSaveAndResume
              ? () => { stepSaveTrigger.current?.(); }
              : undefined
          }
          isAdvancing={isStepPending}
          isSaving={isStepSavePending}
          nextLabel={nextLabel}
          nextDisabled={nextDisabled}
        >
          {activeKey === 'step1' && (
            <Step1Screening
              subjectId={subjectId}
              onComplete={() => handleComplete('step1')}
              triggerRef={stepTrigger}
              onPendingChange={setIsStepPending}
            />
          )}
          {activeKey === 'step2' && (
            <Step3Demographics
              subjectId={subjectId}
              onComplete={() => handleComplete('step2')}
              triggerRef={stepTrigger}
              saveTrigger={stepSaveTrigger}
              onPendingChange={setIsStepPending}
              onSavePendingChange={setIsStepSavePending}
              initialSex={subjectQ.data?.wizard_state.demographics_sex}
              initialGender={subjectQ.data?.wizard_state.demographics_gender}
              initialHeightFt={subjectQ.data?.wizard_state.demographics_height_ft}
              initialHeightIn={subjectQ.data?.wizard_state.demographics_height_in}
              initialWeightLbs={subjectQ.data?.wizard_state.demographics_weight_lbs}
              initialEthnicity={subjectQ.data?.wizard_state.demographics_ethnicity}
              initialRace={subjectQ.data?.wizard_state.demographics_race}
              initialLanguage={subjectQ.data?.wizard_state.demographics_preferred_language}
              initialEmail={
                subjectQ.data?.phi?.unmaskedFields.includes('email_personal')
                  ? subjectQ.data.phi.fields.email_personal ?? null
                  : null
              }
              initialPhone={
                subjectQ.data?.phi?.unmaskedFields.includes('phone_e164')
                  ? subjectQ.data.phi.fields.phone_e164 ?? null
                  : null
              }
            />
          )}
          {activeKey === 'step3' && (
            <Step2InformedConsent
              subjectId={subjectId}
              activeConsentRecordId={isReConsent ? null : (subjectQ.data?.wizard_state.active_consent_record_id ?? null)}
              onComplete={() => handleComplete('step3')}
              triggerRef={stepTrigger}
              saveTrigger={stepSaveTrigger}
              onPendingChange={setIsStepPending}
              onSavePendingChange={setIsStepSavePending}
              onLabelChange={setNextLabel}
              onNextDisabledChange={setNextDisabled}
              initialEmail={
                subjectQ.data?.phi?.unmaskedFields.includes('email_personal')
                  ? subjectQ.data.phi.fields.email_personal ?? null
                  : null
              }
            />
          )}
          {activeKey === 'step5' && (
            <Step5Enroll
              subjectId={subjectId}
              activeConsentRecordId={subjectQ.data?.wizard_state.active_consent_record_id ?? null}
              onComplete={() => handleComplete('step5')}
              triggerRef={stepTrigger}
              onPendingChange={setIsStepPending}
              onLabelChange={setNextLabel}
              onNextDisabledChange={setNextDisabled}
            />
          )}
        </WizardShell>
      )}
    </div>
  );
}
